import { useState } from "react";
import { api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

type ToolType = "spf" | "dkim" | "dmarc" | "full" | "security";

export function PublicToolsPage() {
  const [domain, setDomain] = useState("");
  const [dkimSelector, setDkimSelector] = useState("");
  const [activeTool, setActiveTool] = useState<ToolType>("full");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let data;
      switch (activeTool) {
        case "full":
          data = await api.tools.checkAll(domain);
          break;
        case "spf":
          data = await api.tools.validateSPF(domain);
          break;
        case "dkim":
          if (!dkimSelector) {
            data = await api.tools.discoverDKIM(domain);
          } else {
            data = await api.tools.validateDKIM(domain, dkimSelector);
          }
          break;
        case "dmarc":
          data = await api.tools.validateDMARC(domain);
          break;
        case "security":
          data = await api.tools.securityScan(domain);
          break;
      }
      setResult(data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const tools = [
    { id: "full" as const, name: "Full Check", description: "Check SPF, DKIM & DMARC" },
    { id: "spf" as const, name: "SPF", description: "Validate SPF record" },
    { id: "dkim" as const, name: "DKIM", description: "Validate DKIM record" },
    { id: "dmarc" as const, name: "DMARC", description: "Validate DMARC record" },
    { id: "security" as const, name: "Website Security", description: "Check SSL, headers & blacklists" },
  ];

  // Structured data for the tools page
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "GetSent Email Authentication Checker",
    "description": "Check your email authentication in seconds. Free DMARC, SPF, and DKIM validators - no signup required.",
    "url": "https://getsent.dev/tools",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "DMARC Record Validation",
      "SPF Record Validation",
      "DKIM Record Discovery",
      "Full Email Authentication Check"
    ]
  };

  return (
    <div>
      <SEO
        title="Free DMARC Checker, SPF & DKIM Tools - Instant Email Authentication Check"
        description="Check your email authentication in seconds. Free DMARC, SPF, and DKIM validators - no signup required. Get instant insights."
        canonical="/tools"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-indigo-50/50 to-transparent py-16 dark:from-indigo-950/20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Free DNS{" "}
              <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
                Security Tools
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Check your domain's email authentication setup instantly. No signup required.
            </p>
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          {/* Tool selector */}
          <div className="flex flex-wrap justify-center gap-2">
            {tools.map((tool) => (
              <button
                key={tool.id}
                onClick={() => {
                  setActiveTool(tool.id);
                  setResult(null);
                  setError(null);
                }}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all ${
                  activeTool === tool.id
                    ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25"
                    : "bg-muted hover:bg-muted/80"
                }`}
              >
                {tool.name}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="mt-8 rounded-2xl border bg-card p-6 shadow-lg">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <label className="mb-2 block text-sm font-medium">Domain</label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="example.com"
                  className="w-full rounded-lg border bg-background px-4 py-3 text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  onKeyDown={(e) => e.key === "Enter" && runCheck()}
                />
              </div>
              {activeTool === "dkim" && (
                <div className="sm:w-48">
                  <label className="mb-2 block text-sm font-medium">
                    Selector <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={dkimSelector}
                    onChange={(e) => setDkimSelector(e.target.value)}
                    placeholder="google, default"
                    className="w-full rounded-lg border bg-background px-4 py-3 text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  />
                </div>
              )}
              <div className="flex items-end">
                <button
                  onClick={runCheck}
                  disabled={loading || !domain}
                  className="w-full rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-3 font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-50 sm:w-auto"
                >
                  {loading ? "Checking..." : "Check"}
                </button>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-6 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-600">
              {error}
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="mt-6 space-y-4">
              {activeTool === "full" && <FullCheckResult data={result} />}
              {activeTool === "spf" && <SPFResult data={result} />}
              {activeTool === "dkim" && <DKIMResult data={result} />}
              {activeTool === "dmarc" && <DMARCResult data={result} />}
              {activeTool === "security" && <SecurityScanResult data={result} />}
            </div>
          )}
        </div>
      </section>

      {/* Related Guides */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center mb-8">Learn More About Email Authentication</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Link
              to="/blog/$slug"
              params={{ slug: "how-to-check-dmarc-setup" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="text-xs font-medium text-indigo-600 mb-2">Guide</div>
              <h3 className="font-semibold group-hover:text-indigo-600">How to Check Your DMARC Setup</h3>
              <p className="mt-1 text-sm text-muted-foreground">Step-by-step guide to verify your DMARC configuration is working correctly.</p>
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "free-spf-checker" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="text-xs font-medium text-indigo-600 mb-2">Guide</div>
              <h3 className="font-semibold group-hover:text-indigo-600">Free SPF Checker Guide</h3>
              <p className="mt-1 text-sm text-muted-foreground">Learn how to validate your SPF record and fix common issues.</p>
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "email-authentication-checker" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="text-xs font-medium text-indigo-600 mb-2">Guide</div>
              <h3 className="font-semibold group-hover:text-indigo-600">Email Authentication Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">Is your domain secure? Learn what each authentication protocol does.</p>
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "best-free-dmarc-tools-small-business" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="text-xs font-medium text-indigo-600 mb-2">Comparison</div>
              <h3 className="font-semibold group-hover:text-indigo-600">Best Free DMARC Tools</h3>
              <p className="mt-1 text-sm text-muted-foreground">Compare the best free DMARC tools for small businesses.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Individual Tools */}
      <section className="border-t py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center mb-2">Individual Tool Pages</h2>
          <p className="text-center text-muted-foreground mb-8">
            Need detailed analysis? Visit our dedicated tool pages for in-depth checks and explanations.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/tools/spf-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 mb-3">
                <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <h3 className="font-semibold group-hover:text-indigo-600">SPF Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate SPF records and check DNS lookup limits.
              </p>
            </Link>
            <Link
              to="/tools/dkim-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-purple-500/50 hover:shadow-lg"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10 mb-3">
                <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                </svg>
              </div>
              <h3 className="font-semibold group-hover:text-purple-600">DKIM Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Validate DKIM records and discover selectors.
              </p>
            </Link>
            <Link
              to="/tools/dmarc-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-teal-500/50 hover:shadow-lg"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-500/10 mb-3">
                <svg className="h-5 w-5 text-teal-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <h3 className="font-semibold group-hover:text-teal-600">DMARC Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check DMARC policy and reporting configuration.
              </p>
            </Link>
            <Link
              to="/tools/email-authentication-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 mb-3">
                <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                </svg>
              </div>
              <h3 className="font-semibold group-hover:text-indigo-600">Full Email Check</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check SPF, DKIM, and DMARC all at once.
              </p>
            </Link>
            <Link
              to="/tools/website-security-scanner"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-amber-500/50 hover:shadow-lg"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 mb-3">
                <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.25-8.25-3.286zm0 13.036h.008v.008H12v-.008z" />
                </svg>
              </div>
              <h3 className="font-semibold group-hover:text-amber-600">Security Scanner</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check SSL, security headers, and blacklist status.
              </p>
            </Link>
            <Link
              to="/tools/blacklist-checker"
              className="group rounded-xl border bg-card p-5 transition-all hover:border-red-500/50 hover:shadow-lg"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-500/10 mb-3">
                <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h3 className="font-semibold group-hover:text-red-600">Blacklist Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Check if your domain or IP is on email blacklists.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t py-16">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">
            Want continuous monitoring?
          </h2>
          <p className="mt-2 text-muted-foreground">
            Sign up for free and get automated DMARC report analysis.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-3 font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30"
          >
            Start free monitoring
          </Link>
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status, label }: { status: "valid" | "invalid" | "unknown"; label?: string }) {
  const styles = {
    valid: "bg-green-500/10 text-green-600",
    invalid: "bg-red-500/10 text-red-600",
    unknown: "bg-amber-500/10 text-amber-600",
  };

  const icons = {
    valid: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
      </svg>
    ),
    invalid: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    unknown: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
      </svg>
    ),
  };

  const defaultLabels = {
    valid: "Valid",
    invalid: "Invalid",
    unknown: "Unknown",
  };

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${styles[status]}`}>
      {icons[status]}
      {label || defaultLabels[status]}
    </span>
  );
}

function FullCheckResult({ data }: { data: any }) {
  return (
    <div className="space-y-4">
      {/* SPF */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">SPF Record</h3>
          <StatusBadge status={data.spf?.valid ? "valid" : "invalid"} />
        </div>
        {data.spf?.record && (
          <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
            {data.spf.record}
          </code>
        )}
        {data.spf?.error && (
          <p className="mt-2 text-sm text-red-600">{data.spf.error}</p>
        )}
      </div>

      {/* DKIM */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">DKIM Records</h3>
          <StatusBadge
            status={data.dkim?.selectors?.length > 0 ? "valid" : "unknown"}
            label={data.dkim?.selectors?.length > 0 ? `${data.dkim.selectors.length} found` : "Unknown"}
          />
        </div>
        {data.dkim?.selectors?.length > 0 ? (
          data.dkim.selectors.map((sel: any, i: number) => (
            <div key={i} className="mt-3 rounded-lg bg-muted p-3">
              <div className="text-sm font-medium">{sel.selector}</div>
              <code className="mt-1 block overflow-x-auto text-xs text-muted-foreground">
                {sel.record?.substring(0, 100)}...
              </code>
            </div>
          ))
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            No common DKIM selectors found. Your domain may still have DKIM configured with a custom selector.
            Use the <strong>DKIM tool</strong> above and enter your selector name to verify.
          </p>
        )}
      </div>

      {/* DMARC */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">DMARC Record</h3>
          <StatusBadge status={data.dmarc?.valid ? "valid" : "invalid"} />
        </div>
        {data.dmarc?.record && (
          <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
            {data.dmarc.record}
          </code>
        )}
        {data.dmarc?.policy && (
          <div className="mt-3 flex gap-4 text-sm">
            <span>Policy: <strong>{data.dmarc.policy}</strong></span>
            {data.dmarc.pct && <span>Percentage: <strong>{data.dmarc.pct}%</strong></span>}
          </div>
        )}
        {data.dmarc?.error && (
          <p className="mt-2 text-sm text-red-600">{data.dmarc.error}</p>
        )}
      </div>
    </div>
  );
}

function SPFResult({ data }: { data: any }) {
  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">SPF Record</h3>
        <StatusBadge status={data.valid ? "valid" : "invalid"} />
      </div>
      {data.record && (
        <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
          {data.record}
        </code>
      )}
      {data.lookupCount !== undefined && (
        <p className="mt-3 text-sm">
          DNS Lookups: <strong>{data.lookupCount}/10</strong>
          {data.lookupCount > 10 && (
            <span className="ml-2 text-red-600">(exceeds limit!)</span>
          )}
        </p>
      )}
      {data.includes?.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-medium">Includes:</p>
          <ul className="mt-1 text-sm text-muted-foreground">
            {data.includes.map((inc: string, i: number) => (
              <li key={i}>{inc}</li>
            ))}
          </ul>
        </div>
      )}
      {data.errors?.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-medium text-red-600">Errors:</p>
          <ul className="mt-1 text-sm text-red-600">
            {data.errors.map((err: string, i: number) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DKIMResult({ data }: { data: any }) {
  const isDiscovery = Array.isArray(data.selectors);

  if (isDiscovery) {
    return (
      <div className="rounded-xl border bg-card p-6">
        <h3 className="text-lg font-semibold">DKIM Discovery</h3>
        {data.selectors?.length > 0 ? (
          <div className="mt-4 space-y-3">
            {data.selectors.map((sel: any, i: number) => (
              <div key={i} className="rounded-lg bg-muted p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{sel.selector}</span>
                  <StatusBadge status={sel.valid ? "valid" : "invalid"} />
                </div>
                {sel.record && (
                  <code className="mt-2 block overflow-x-auto text-xs text-muted-foreground">
                    {sel.record.substring(0, 150)}...
                  </code>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-muted-foreground">
            No common DKIM selectors found. Try entering a specific selector.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">DKIM Record</h3>
        <StatusBadge status={data.valid ? "valid" : "invalid"} />
      </div>
      {data.record && (
        <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
          {data.record}
        </code>
      )}
      {data.error && (
        <p className="mt-2 text-sm text-red-600">{data.error}</p>
      )}
    </div>
  );
}

function DMARCResult({ data }: { data: any }) {
  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">DMARC Record</h3>
        <StatusBadge status={data.valid ? "valid" : "invalid"} />
      </div>
      {data.record && (
        <code className="mt-3 block overflow-x-auto rounded-lg bg-muted p-3 text-sm">
          {data.record}
        </code>
      )}
      {data.parsed && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Policy (p)</div>
            <div className="font-semibold">{data.parsed.p || "none"}</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Subdomain Policy (sp)</div>
            <div className="font-semibold">{data.parsed.sp || "inherit"}</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Percentage (pct)</div>
            <div className="font-semibold">{data.parsed.pct || "100"}%</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Aggregate Reports (rua)</div>
            <div className="truncate font-semibold">{data.parsed.rua || "not set"}</div>
          </div>
        </div>
      )}
      {data.warnings?.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-yellow-600">Warnings:</p>
          <ul className="mt-1 text-sm text-yellow-600">
            {data.warnings.map((w: string, i: number) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      {data.errors?.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-red-600">Errors:</p>
          <ul className="mt-1 text-sm text-red-600">
            {data.errors.map((e: string, i: number) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SecurityScanResult({ data }: { data: any }) {
  const gradeColors: Record<string, string> = {
    "A+": "text-green-600 bg-green-500/10",
    "A": "text-green-600 bg-green-500/10",
    "B": "text-lime-600 bg-lime-500/10",
    "C": "text-amber-600 bg-amber-500/10",
    "D": "text-orange-600 bg-orange-500/10",
    "F": "text-red-600 bg-red-500/10",
  };

  const headerStatusIcon = (status: string) => {
    if (status === "pass") {
      return (
        <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      );
    } else if (status === "warning") {
      return (
        <svg className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  };

  return (
    <div className="space-y-4">
      {/* Overall Score */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Security Score</h3>
            <p className="text-sm text-muted-foreground">{data.domain}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-3xl font-bold">{data.overallScore}</div>
              <div className="text-sm text-muted-foreground">out of 100</div>
            </div>
            <div className={`rounded-lg px-4 py-2 text-2xl font-bold ${gradeColors[data.grade] || "text-gray-600 bg-gray-500/10"}`}>
              {data.grade}
            </div>
          </div>
        </div>
        {data.cached && (
          <p className="mt-2 text-xs text-muted-foreground">
            Cached result from {new Date(data.scannedAt).toLocaleString()}
          </p>
        )}
      </div>

      {/* SSL/TLS */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">SSL/TLS Certificate</h3>
          <StatusBadge status={data.ssl.valid ? "valid" : "invalid"} />
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Issuer</div>
            <div className="font-semibold truncate">{data.ssl.issuer || "Unknown"}</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Protocol</div>
            <div className="font-semibold">{data.ssl.protocol || "Unknown"}</div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Expires</div>
            <div className="font-semibold">
              {data.ssl.expiresAt
                ? new Date(data.ssl.expiresAt).toLocaleDateString()
                : "Unknown"}
            </div>
          </div>
          <div className="rounded-lg bg-muted p-3">
            <div className="text-xs text-muted-foreground">Days Until Expiry</div>
            <div className={`font-semibold ${
              data.ssl.daysUntilExpiry !== null && data.ssl.daysUntilExpiry < 30
                ? "text-amber-600"
                : ""
            }`}>
              {data.ssl.daysUntilExpiry ?? "Unknown"}
            </div>
          </div>
        </div>
        {data.ssl.errors?.length > 0 && (
          <div className="mt-3 text-sm text-red-600">
            {data.ssl.errors.map((e: string, i: number) => <p key={i}>{e}</p>)}
          </div>
        )}
        {data.ssl.warnings?.length > 0 && (
          <div className="mt-3 text-sm text-amber-600">
            {data.ssl.warnings.map((w: string, i: number) => <p key={i}>{w}</p>)}
          </div>
        )}
      </div>

      {/* Security Headers */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Security Headers</h3>
          <span className="text-sm text-muted-foreground">
            Score: {data.headers.score}/100
          </span>
        </div>
        <div className="mt-4 space-y-2">
          {data.headers.headers?.map((header: any, i: number) => (
            <div key={i} className="flex items-start gap-3 rounded-lg bg-muted p-3">
              <div className="mt-0.5">{headerStatusIcon(header.status)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{header.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    header.importance === "critical" ? "bg-red-500/10 text-red-600" :
                    header.importance === "high" ? "bg-orange-500/10 text-orange-600" :
                    header.importance === "medium" ? "bg-amber-500/10 text-amber-600" :
                    "bg-gray-500/10 text-gray-600"
                  }`}>
                    {header.importance}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{header.description}</p>
                {header.status !== "pass" && header.recommendation && (
                  <p className="mt-1 text-xs text-amber-600">{header.recommendation}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Blacklist Status */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Blacklist Status</h3>
          <StatusBadge
            status={data.blacklist.clean ? "valid" : "invalid"}
            label={data.blacklist.clean ? "Clean" : "Listed"}
          />
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Checked {data.blacklist.checked?.length || 0} blocklists
        </p>
        {data.blacklist.detectedOn?.length > 0 && (
          <div className="mt-3 rounded-lg bg-red-500/10 p-3 text-sm text-red-600">
            <p className="font-medium">Listed on:</p>
            <ul className="mt-1 list-disc list-inside">
              {data.blacklist.detectedOn.map((list: string, i: number) => (
                <li key={i}>{list}</li>
              ))}
            </ul>
          </div>
        )}
        {data.blacklist.errors?.length > 0 && (
          <div className="mt-3 text-sm text-amber-600">
            {data.blacklist.errors.map((e: string, i: number) => <p key={i}>{e}</p>)}
          </div>
        )}
      </div>
    </div>
  );
}
